
import { Component, Emit, Prop, Provide, Ref, Vue, Watch } from 'vue-property-decorator';

import { BASIC_DATA_FIELDS } from '@/enums';
import { VForm } from '@/types';
import { filterGeocodeResultTypes, formatResult } from '@/utils/map.util';

declare global {
  interface Window {
    google: {
      maps: {
        Geocoder: () => void;
        Circle: ({ center, radius }: { center: any; radius: any }) => void;
        places: {
          Autocomplete: (id: VForm | null, options: any) => void;
        };
        LatLngBounds: () => void;
      };
      places: any;
    };
  }
}

@Component({
  components: {
  }
})
export default class GoogleAutocomplete extends Vue {
  @Prop({ type: String, required: true }) id: string;
  @Prop({ type: String, default: 'Start typing' }) classname: string;
  @Prop({ type: String }) placeholder: string;
  @Prop({ type: Boolean }) disabled: boolean;
  @Prop({ type: String, default: 'address' }) types: string;
  @Prop({ type: String, default: 'address' }) geoTypes: string;
  @Prop({ type: Array, default: () => BASIC_DATA_FIELDS }) fields: string[];
  @Prop({ type: [String, Array], default: null }) country: [string, string[]];
  @Prop({ type: Boolean, default: false }) enableGeolocation: boolean;
  @Prop({ type: Object, default: null }) geolocationOptions: any;

  @Ref('autocomplete') readonly autocompleteRef!: VForm
  autoComInst: any;
  autocomplete: any;
  /**
   * The Autocomplete object.
   *
   * @type {Autocomplete}
   * @link https://developers.google.com/maps/documentation/javascript/reference#Autocomplete
   */

  /**
   * Autocomplete input text
   * @type {String}
   */
  @Provide() autocompleteText = '';
  @Provide() geolocation: {
    /**
     * Google Geocoder Objet
     * @type {Geocoder}
     * @link https://developers.google.com/maps/documentation/javascript/reference#Geocoder
     */
    geocoder?: {
      geocode: (options: any, callback: (results: any, status: any) => void) => void;
    };
    /**
     * Filled after geolocate result
     * @type {Coordinates}
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Coordinates
     */
    loc: {
      lat: number| null;
      lng: number| null;
    };
    /**
     * Filled after geolocate result
     * @type {Position}
     * @link https://developer.mozilla.org/en-US/docs/Web/API/Position
     */
    // TODO~> better interface
    position?: { coords: Coords };
  } = {
    loc: { lat: null, lng: null }
  }

  $gmapApiPromiseLazy: any;

  async mounted() {
    await this.$gmapApiPromiseLazy()
    this.$nextTick(async () => {
      const options: any = {};
      if (this.types) {
        options.types = [this.types];
      }
      if (this.country) {
        options.componentRestrictions = {
          country: this.country
        };
      }

      this.autocomplete = new window.google.maps.places.Autocomplete(
        this.autocompleteRef,
        options
      );
      this.autocomplete.setFields(this.fields);
      this.autocomplete.addListener('place_changed', this.onPlaceChanged);
    })
  }

  @Watch('types')
  watchTypes(newVal: string) {
    this.autocompleteText = '';
    this.autocomplete.setTypes([newVal]);
  }

  @Watch('autocompleteText')
  onAutocompleteTextChange(newVal: string, oldVal: string) {
    this.$emit('inputChange', { newVal, oldVal }, this.id);
  }

  @Watch('country')
  onCountryChange() {
    this.autocomplete.setComponentRestrictions({
      country: this.country === null ? [] : this.country
    });
  }

  /**
   * When a place changed
   */
  onPlaceChanged() {
    const place = this.autocomplete.getPlace();
    if (!place.geometry) {
      // User entered the name of a Place that was not suggested and
      // pressed the Enter key, or the Place Details request failed.
      this.$emit('no-results-found', place, this.id);
      return;
    }
    if (place.address_components !== undefined) {
      // return returnData object and PlaceResult object
      this.$emit('placechanged', formatResult(place), place, this.id);
      // update autocompleteText then emit change event
      this.autocompleteText = this.autocompleteRef.value
      this.onChange()
    }
  }

  /**
   * When the input gets focus
   */
  @Emit('focus')
  onFocus() {
    this.biasAutocompleteLocation();
  }

  /**
   * When the input loses focus
   */
  @Emit('blur')
  onBlur() {
    //
  }

  /**
   * When the input got changed
   */
  @Emit('change')
  onChange() {
    this.$emit('change', this.autocompleteText);
  }

  /**
   * When a key gets pressed
   * @param  {Event} event A keypress event
   */
  @Emit('keypress')
  onKeyPress() {
    //
  }

  /**
   * When a keyup occurs
   * @param  {Event} event A keyup event
   */
  @Emit('keyup')
  onKeyUp() {
    //
  }

  /**
   * Clear the input
   */
  clear() {
    this.autocompleteText = ''
  }

  /**
   * Focus the input
   */

  focus() {
    this.autocompleteRef.focus()
  }

  /**
   * Blur the input
   */
  blur() {
    this.autocompleteRef.blur()
  }

  /**
   * Update the value of the input
   * @param  {String} value
   */
  update(value) {
    this.autocompleteText = value
  }

  /**
   * Update the coordinates of the input
   * @param  {Coordinates} value
   */
  updateCoordinates(value) {
    if (!value && !(value.lat || value.lng)) return;
    if (!this.geolocation.geocoder) this.geolocation.geocoder = new window.google.maps.Geocoder();
    (this.geolocation.geocoder as any).geocode({ location: value }, (results, status) => {
      if (status === 'OK') {
        results = filterGeocodeResultTypes(results, this.geoTypes);
        if (results[0]) {
          this.$emit('updateCoordinates', formatResult(results[0]), results[0], this.id);
        } else {
          this.$emit('error', 'no result for provided coordinates');
        }
      } else {
        this.$emit('error', 'error getting address from coords');
      }
      this.$emit('locating', false);
    })
  }

  /**
   * Update location based on navigator geolocation
   */
  geolocateUser() {
    this.updateGeolocation((geolocation) => {
      this.updateCoordinates(geolocation)
    })
  }

  /**
   * Update internal location from navigator geolocation
   * @param  {Function} (geolocation, position)
   */
  updateGeolocation(callback: any = null) {
    if (navigator.geolocation) {
      const options = {};
      if (this.geolocationOptions) Object.assign(options, this.geolocationOptions);
      navigator.geolocation.getCurrentPosition(position => {
        const geolocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.$log.info('geolocation', { geolocation, position });
        this.geolocation.loc = geolocation;
        this.geolocation.position = position;
        if (callback) callback(geolocation, position);
      }, err => {
        if (!err.message.includes('User denied Geolocation')) {
          this.$emit('error', err.message);
        }
        this.$emit('locating', false);
        // if (err.message.includes('User denied Geolocation') && window.confirm('You have denied permissions. go to chrome://settings/content/location to enable permissions')) {
        // }
      }, options);
    }
  }

  // Bias the autocomplete object to the user's geographical location,
  // as supplied by the browser's 'navigator.geolocation' object.
  biasAutocompleteLocation() {
    if (this.enableGeolocation) {
      this.updateGeolocation((geolocation, position) => {
        const circle = new window.google.maps.Circle({
          center: geolocation,
          radius: position.coords.accuracy
        });
        this.autocomplete.setBounds(circle.getBounds());
      })
    }
  }
}
